'use client';
import { type ComponentPropsWithoutRef } from 'react';
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { Combobox as BaseCombobox } from '@/slw/shared/ui/combobox';
import { cn } from '@/slw/shared/lib/utils';
import { SelectOption } from '@/slw/shared/types/common.type';

interface ComboboxProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseCombobox>, 'name' | 'defaultValue' | 'value' | 'onValueChange'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  enableCheck?: boolean;
  containerClass?: string;
  noResultText?: string;
  onValueChange?: (value: string) => void;
  withError?: boolean;
  popoverClassName?: string;
  showMessError?: boolean;
  direction?: 'row' | 'column';
  className?: string;
  labelClass?: string;
  required?: boolean;
}

export function Combobox<TFieldValues extends FieldValues>({
  name,
  label,
  helperText,
  defaultValue,
  containerClass,
  options,
  placeholder = '',
  withError = true,
  onValueChange,
  popoverClassName,
  showMessError = true,
  className,
  labelClass,
  direction = 'row',
  required = false,
  ...props
}: ComboboxProps<TFieldValues>) {
  const { control } = useFormContext();

  const handleChange = (value: string) => {
    onValueChange && onValueChange(value);
  };

  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && (
            <FormLabel className={cn('whitespace-nowrap font-normal text-sm leading-[1.5]', labelClass)}>
              {label}
              {required && <span className='text-[#dc2626]'>*</span>}
            </FormLabel>
          )}
          <BaseCombobox
            options={options}
            popoverClassName={popoverClassName}
            placeholder={placeholder}
            value={field.value}
            onChange={(value: string) => {
              field.onChange(value);
              handleChange(value);
            }}
            {...props}
            className={cn(className, error && withError && 'border-destructive focus-visible:ring-destructive')}
          />
          {helperText && withError && showMessError && (
            <p className='text-sm text-muted-foreground ml-1.5'>{helperText}</p>
          )}
          {error && withError && showMessError && <FormMessage>{error.message}</FormMessage>}
        </FormItem>
      )}
    />
  );
}
